<template>
  <article class="flight-booking-card" :aria-labelledby="headingId">
    <header class="flight-booking-card__header">
      <div class="flight-booking-card__identity">
        <div class="flight-booking-card__reference-group">
          <span v-if="booking.booking_mode" class="flight-booking-card__mode" role="img" :aria-label="`Booking mode: ${modeLabel}`" :title="modeLabel">
            <i class="pi" :class="isManual ? 'pi-pencil' : 'pi-bolt'" aria-hidden="true" />
          </span>
          <h2 :id="headingId" class="flight-booking-card__title">
            <router-link v-if="canOpenBooking" :to="{ name: 'flights-bookings-detail', params: { id: booking.id } }">
              Reservation #ATF-{{ booking.id }}
            </router-link>
            <span v-else>Reservation #ATF-{{ booking.id }}</span>
          </h2>
        </div>
        <span class="flight-booking-card__airline">
          <span class="flight-booking-card__logo-slot" :class="{ 'flight-booking-card__logo-slot--wordmark': airline.name === 'Air Tanzania' }" aria-hidden="true">
            <img v-if="airline.logo_url && airline.logo_url !== failedLogo" :src="airline.logo_url" alt="" class="flight-booking-card__logo" @error="failedLogo = airline.logo_url" />
            <span v-else class="flight-booking-card__logo-fallback">{{ airlineInitial }}</span>
          </span>
          <span>{{ airline.name || '—' }}</span>
        </span>
      </div>
      <div class="flight-booking-card__aside">
        <dl v-if="deadlines.length" class="flight-booking-card__deadlines" aria-label="Booking deadlines">
          <div v-for="limit in deadlines" :key="limit.label" class="flight-booking-card__field" :data-deadline-state="deadlineState(limit.value)">
            <dt :title="limit.title">{{ limit.label }}:</dt>
            <dd class="flight-booking-card__deadline-value">
              <time :datetime="limit.value" :title="deadlineDescription(limit.value)">{{ deadline(limit.value) }}</time>
            </dd>
          </div>
        </dl>
        <span class="flight-booking-card__status" :style="statusStyle(status)">{{ status.name || status.code || '—' }}</span>
      </div>
    </header>

    <dl v-if="isManual" class="flight-booking-card__manual" aria-label="Manual booking statuses">
      <div class="flight-booking-card__field">
        <dt>Provider Status:</dt>
        <dd><span class="flight-booking-card__status" :style="statusStyle(providerStatus)">{{ providerStatus.name || providerStatus.code || '—' }}</span></dd>
      </div>
      <div class="flight-booking-card__field">
        <dt>Request Status:</dt>
        <dd><span class="flight-booking-card__status" :style="statusStyle(requestStatus)">{{ requestStatus.name || requestStatus.code || '—' }}</span></dd>
      </div>
    </dl>

    <dl class="flight-booking-card__metadata">
      <div class="flight-booking-card__field flight-booking-card__pnr">
        <dt>PNR:</dt>
        <dd>{{ booking.pnr || '—' }}</dd>
      </div>
      <div class="flight-booking-card__field flight-booking-card__passengers">
        <dt>Passengers:</dt>
        <dd>
          <span class="flight-booking-card__adults">{{ adultCount }} {{ booking.adult === 1 ? 'Adult' : 'Adults' }}</span>
          <template v-if="booking.child > 0"><span class="flight-booking-card__separator" aria-hidden="true">·</span><span>{{ booking.child }} {{ booking.child === 1 ? 'Child' : 'Children' }}</span></template>
          <template v-if="booking.infant > 0"><span class="flight-booking-card__separator" aria-hidden="true">·</span><span>{{ booking.infant }} {{ booking.infant === 1 ? 'Infant' : 'Infants' }}</span></template>
        </dd>
      </div>
      <div v-if="showLead && (booking.lead || booking.group_name)" class="flight-booking-card__field flight-booking-card__owner">
        <dt>{{ booking.lead ? 'Lead:' : 'Group:' }}</dt>
        <dd>
          <span v-if="manager" class="flight-booking-card__avatar" role="img" :aria-label="`Manager: ${managerName || '—'}`" :title="`Manager: ${managerName || '—'}`">
            <img v-if="managerPhoto && managerPhoto !== failedPhoto" :src="managerPhoto" alt="" @error="failedPhoto = managerPhoto" />
            <span v-else aria-hidden="true">{{ managerInitials }}</span>
          </span>
          <span>{{ booking.lead ? booking.lead.name || `Lead #${booking.lead.id}` : booking.group_name }}</span>
        </dd>
      </div>
    </dl>

    <ol v-if="flights.length" class="flight-booking-card__flights" aria-label="Flight segments">
      <flight-segment-row
        v-for="(flight, index) in flights" :key="flight.id || index"
        :date="flight.date" :origin="flight.from" :destination="flight.to"
        :departure="flight.departure" :arrival="flight.arrival"
      />
    </ol>
    <p v-else class="flight-booking-card__empty">No flight segments available.</p>
  </article>
</template>

<script>
import { dateTime, deadlineState } from '@/views/flights/bookings/booking-detail'
import FlightSegmentRow from '@/components/flights/FlightSegmentRow.vue'

export default {
  name: 'FlightBookingCard',
  components: { FlightSegmentRow },
  props: {
    booking: { type: Object, required: true },
    showLead: { type: Boolean, default: true },
    canOpenBooking: { type: Boolean, default: true }
  },
  data() {
    return { failedPhoto: '', failedLogo: '', now: Date.now(), deadlineTimer: null }
  },
  computed: {
    headingId() { return `flight-booking-${this.booking.id}` },
    status() { return this.booking.status || {} },
    isManual() { return this.booking.booking_mode && this.booking.booking_mode.code === 'manual' },
    modeLabel() { return this.isManual ? 'Manual' : 'Auto' },
    airline() { return this.booking.airline || {} },
    airlineInitial() {
      const name = (this.airline.name || '').trim()
      return name ? name.charAt(0).toUpperCase() : '—'
    },
    adultCount() { return this.booking.adult === null || this.booking.adult === undefined ? '—' : this.booking.adult },
    deadlines() {
      if (!['pending', 'held'].includes(this.status.code)) return []
      return [
        { label: 'PTL', title: 'Payment time limit', value: this.booking.payment_time_limit },
        { label: 'TTL', title: 'Ticketing time limit', value: this.booking.ticketing_time_limit }
      ].filter(limit => Boolean(dateTime(limit.value)))
    },
    providerStatus() { return (this.booking.manual_booking && this.booking.manual_booking.provider_status) || {} },
    requestStatus() { return (this.booking.manual_booking && this.booking.manual_booking.request_status) || {} },
    manager() { return this.booking.lead && this.booking.lead.manager },
    managerName() {
      if (!this.manager) return ''
      return (this.manager.full_name || [this.manager.first_name, this.manager.last_name].filter(Boolean).join(' ')).trim()
    },
    managerPhoto() { return (this.manager && this.manager.crop && this.manager.crop.crop_url) || '' },
    managerInitials() {
      const names = this.managerName.split(/\s+/).filter(Boolean)
      return names.length ? [names[0][0], names.length > 1 ? names[names.length - 1][0] : ''].join('').toUpperCase() : '—'
    },
    flights() { return Array.isArray(this.booking.flights) ? this.booking.flights : [] }
  },
  mounted() {
    this.deadlineTimer = setInterval(() => { this.now = Date.now() }, 60000)
  },
  beforeDestroy() {
    clearInterval(this.deadlineTimer)
  },
  methods: {
    deadline(value) { return dateTime(value) || '—' },
    deadlineState(value) {
      return deadlineState(value, this.now)
    },
    deadlineDescription(value) {
      const state = this.deadlineState(value)
      if (state === 'expired') return 'Deadline has passed'
      return state === 'due-soon' ? 'Less than 48 hours remaining' : ''
    },
    statusStyle(value) { return value.color ? { backgroundColor: value.color, color: '#fff' } : null }
  }
}
</script>

<style lang="scss" scoped>
.flight-booking-card {
  --booking-surface: #fff;
  --booking-soft: #f7f8f5;
  --booking-ink: #20241d;
  --booking-muted: #62685d;
  --booking-line: #dce0d6;
  --booking-green: #527b12;
  --booking-green-bg: #f0f6e7;
  --booking-warning: #b8860b;
  --booking-red: #b03932;
  min-width: 0;
  padding: 17px 18px;
  border: 1px solid var(--booking-line);
  border-radius: 8px;
  background: var(--booking-surface);
  color: var(--booking-ink);
  font: 400 15px/1.45 $font-family-base;
}

.flight-booking-card__header,
.flight-booking-card__identity,
.flight-booking-card__aside,
.flight-booking-card__deadlines,
.flight-booking-card__manual,
.flight-booking-card__metadata {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  min-width: 0;
  gap: 10px 20px;
  margin: 0;
}

.flight-booking-card__header { align-items: flex-start; gap: 10px 24px; }
.flight-booking-card__identity { flex: 1 1 auto; gap: 6px 10px; }
.flight-booking-card__reference-group { display: inline-flex; align-items: center; gap: 8px; }
.flight-booking-card__mode {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 26px;
  width: 26px;
  height: 26px;
  border-radius: 5px;
  background: var(--booking-soft);
  color: var(--booking-muted);

  .pi { font-size: 17px; }
  .pi-pencil { font-size: 14px; }
}
.flight-booking-card__title {
  margin: 0;
  min-width: 0;
  font: inherit;
  font-weight: 500;

  a {
    display: inline-flex;
    align-items: center;
    min-height: 28px;
    color: var(--booking-green);
    text-decoration: underline;
    text-decoration-color: var(--booking-line);
    text-underline-offset: 4px;
    overflow-wrap: anywhere;

    &:hover { text-decoration-color: var(--booking-green); }
    &:focus-visible { outline: 2px solid var(--booking-green); outline-offset: 4px; border-radius: 2px; }
  }
}
.flight-booking-card__airline { display: inline-flex; align-items: center; gap: 5px; color: var(--booking-muted); }
.flight-booking-card__logo-slot { display: inline-flex; align-items: center; justify-content: center; width: 28px; height: 24px; flex-shrink: 0; }
.flight-booking-card__logo-slot--wordmark { width: 56px; }
.flight-booking-card__logo { display: block; width: 100%; height: 100%; object-fit: contain; }
.flight-booking-card__logo-fallback {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border: 1px solid var(--booking-line);
  border-radius: 50%;
  background: var(--booking-soft);
  color: var(--booking-muted);
  font-size: 12px;
  font-weight: 500;
}
.flight-booking-card__aside { justify-content: flex-end; gap: 10px 16px; margin-left: auto; }
.flight-booking-card__deadlines { justify-content: flex-end; gap: 8px 16px; font-variant-numeric: tabular-nums; }
.flight-booking-card__deadlines time { white-space: nowrap; }
.flight-booking-card__deadline-value { display: inline-flex; align-items: center; gap: 7px; }
.flight-booking-card__field[data-deadline-state='expired'] time { color: var(--booking-red); }
.flight-booking-card__field[data-deadline-state='due-soon'] time { color: var(--booking-warning); }

.flight-booking-card__status {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 8px;
  border-radius: 5px;
  background: var(--booking-soft);
  color: var(--booking-muted);
  font-weight: 500;
  white-space: nowrap;

}
.flight-booking-card__manual {
  font-size: 14px;
  width: fit-content;
  max-width: 100%;
  gap: 8px 24px;
  margin-top: 10px;
  padding: 8px 12px;
  border-radius: 6px;
  background: var(--booking-soft);

  .flight-booking-card__status { white-space: normal; }
  .flight-booking-card__status { background: var(--booking-surface); }
}
.flight-booking-card__metadata { gap: 8px 20px; margin-top: 12px; }
.flight-booking-card__field {
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;

  dt { flex-shrink: 0; color: var(--booking-muted); }
  dd { min-width: 0; margin: 0; font-weight: 500; overflow-wrap: anywhere; }
}
.flight-booking-card__pnr { flex: 0 0 136px; font-variant-numeric: tabular-nums; }
.flight-booking-card__passengers {
  flex: 0 0 auto;
  gap: 6px;

  dd { display: flex; align-items: center; flex-wrap: wrap; gap: 6px; font-weight: 400; }
  dd > span { white-space: nowrap; }
}
.flight-booking-card__adults { min-width: 62px; }
.flight-booking-card__separator { color: var(--booking-muted); }
.flight-booking-card__owner {
  flex: 1 1 0;
  gap: 7px;

  dd { display: flex; align-items: center; gap: 7px; }
}
.flight-booking-card__avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 24px;
  width: 24px;
  height: 24px;
  overflow: hidden;
  border: 1px solid var(--booking-line);
  border-radius: 50%;
  background: var(--booking-green-bg);
  color: var(--booking-green);
  font-size: 10px;

  img { width: 100%; height: 100%; object-fit: cover; object-position: 50% 30%; }
}
.flight-booking-card__flights { list-style: none; margin: 14px 0 0; padding: 0; border-top: 1px solid var(--booking-line); }
.flight-booking-card__empty { margin: 14px 0 0; padding-top: 12px; border-top: 1px solid var(--booking-line); color: var(--booking-muted); }

@media (max-width: 720px) {
  .flight-booking-card__owner { flex-basis: 100%; }
  .flight-booking-card__passengers { flex-shrink: 1; }
}

@media (max-width: 520px) {
  .flight-booking-card { padding: 13px 12px; }
  .flight-booking-card__aside { width: 100%; }
  .flight-booking-card__metadata { gap: 7px 15px; margin-top: 11px; }
  .flight-booking-card__manual .flight-booking-card__field { flex-wrap: wrap; }
  .flight-booking-card__flights { margin-top: 12px; }
}

@media (pointer: coarse) {
  .flight-booking-card__title a { min-height: 44px; }
}
</style>
